import { useCalendarEvents } from "@/hooks/useEvents";
import { usePageTitle } from "@/hooks/usePageTitle";
import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Scheduler } from "calendarkit-pro";
import type { CalendarEvent, ViewType } from "calendarkit-pro";
import { Button } from "@/components/ui/button";
import { Settings, Expand, Shrink } from "lucide-react";
import { CalendarSettings } from "@/components/calendar/CalendarSettings";
import { useSettings } from "@/providers/SettingsProvider";
import { CreateEventModal } from "@/components/calendar/CreateEventModal";
import { useCalendarState } from "@/hooks/useCalendarState";
import { EventObject } from "@/components/calendar/types";
import { usePersistedCalendarView, usePersistedSidebarExpanded } from "@/utils/calendarPersistence";
import { createRoot } from "react-dom/client";
import { startOfWeek, addDays, isSameDay, startOfDay } from "date-fns";
import { enGB, enUS } from "date-fns/locale";
import type { Locale } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useEntityHistory } from "@/hooks/useEntityHistory";

// Wrapper component for CalendarKit's event form that manages URL state
const EventFormRenderer = ({
	isOpen,
	onClose,
	event,
	initialDate,
	onSave,
	onDelete,
	calendarEvents,
	refetch,
	isDeletingEvent,
	handleCalendarKitEventDelete,
	onOpenEvent,
	onCloseEvent,
	onTrackCalendarKitEvent
}: any) => {
	// Set URL when modal opens with an event and track CalendarKit's open event
	useEffect(() => {
		if (isOpen && event?.id) {
			onOpenEvent(event.id);
			onTrackCalendarKitEvent(event.id);
		} else if (!isOpen) {
			onTrackCalendarKitEvent(null);
		}
	}, [isOpen, event?.id, onOpenEvent, onTrackCalendarKitEvent]);

	if (!isOpen) return null;

	// If event exists, it's editing mode
	if (event && event.id) {
		// Find the original event to preserve all metadata
		const originalEvent = calendarEvents.find((e: any) => e.id === event.id);
		if (originalEvent) {
			return (
				<CreateEventModal
					initialData={{
						id: originalEvent.id,
						title: originalEvent.title,
						description: originalEvent.description,
						start: originalEvent.start,
						end: originalEvent.end,
						color: originalEvent.color,
						location: originalEvent.location,
						url: originalEvent.url,
						status: originalEvent.status,
						priority: originalEvent.priority,
						all_day: originalEvent.all_day,
						metadata: originalEvent.metadata || {
							candidates: [],
							offers: [],
							companies: [],
							matches: [],
							contacts: []
						}
					}}
					isOpen={isOpen}
					onClose={() => {
						onCloseEvent();
						onClose();
					}}
					onSave={async () => {
						await refetch();
						onCloseEvent();
						onClose();
					}}
					onDelete={async () => {
						await handleCalendarKitEventDelete(event.id);
						onCloseEvent();
						onClose();
					}}
					isEditing={true}
					isDeleting={isDeletingEvent}
				/>
			);
		}
	}

	// Otherwise, it's create mode
	const startDate = initialDate || event?.start || new Date();
	const endDate = event?.end || new Date(startDate.getTime() + 60 * 60 * 1000);

	return (
		<CreateEventModal
			isOpen={isOpen}
			onClose={onClose}
			onSave={async () => {
				await refetch();
				onClose();
			}}
			initialDate={startDate}
			initialData={{
				id: '',
				title: '',
				description: '',
				start: startDate,
				end: endDate,
				color: '#9b87f5',
				metadata: {
					candidates: [],
					offers: [],
					companies: [],
					matches: [],
					contacts: []
				}
			}}
		/>
	);
};

const Calendar = () => {
	const [searchParams, setSearchParams] = useSearchParams();
	const [selectedEventFromUrl, setSelectedEventFromUrl] = useState<EventObject | null>(null);
	const [calendarKitOpenEventId, setCalendarKitOpenEventId] = useState<string | null>(null);
	const {
		events: calendarEvents,
		isLoading,
		refetch
	} = useCalendarEvents();
	const { trackVisit } = useEntityHistory();

	// Get event ID from URL
	const eventIdFromUrl = searchParams.get('event');

	// Update page title based on selected event
	usePageTitle(
		selectedEventFromUrl ? `${selectedEventFromUrl.title} - Calendar` : "Calendar"
	);
	const { timezone, calendarSettings } = useSettings();
	const [showSettings, setShowSettings] = useState(false);
	const [isUpdating, setIsUpdating] = useState(false);
	const [showCreateModal, setShowCreateModal] = useState(false);
	const [selectedSlotDate, setSelectedSlotDate] = useState<Date | undefined>(undefined);
	const [view, setView] = usePersistedCalendarView("week");
	const [sidebarExpanded, setSidebarExpanded] = useState(false); // Temporarily hardcoded to test
	const [date, setDate] = useState(new Date());
	const schedulerContainerRef = useRef<HTMLDivElement>(null);
	const settingsButtonRootRef = useRef<any>(null);
	const [showAllHours, setShowAllHours] = useState(false);
	const [createAllDayDate, setCreateAllDayDate] = useState<Date | null>(null);
	const allDayRowRootRef = useRef<any>(null);

	// Track if we've done the initial scroll (to avoid re-scrolling on every render)
	const hasInitialScrolled = useRef(false);
	// Track the last scroll position to restore after updates
	const lastScrollPositionRef = useRef<number | null>(null);

	// Handle URL parameter for opening event
	useEffect(() => {
		if (!eventIdFromUrl) {
			if (selectedEventFromUrl)
				setSelectedEventFromUrl(null);
			return;
		}

		// Skip if we already have this event selected
		if (selectedEventFromUrl?.id === eventIdFromUrl)
			return;

		// First check if event is in current list
		const event = calendarEvents.find(e => e.id === eventIdFromUrl);
		if (event) {
			setSelectedEventFromUrl(event);
			trackVisit('event', event.id, event.title);
			return;
		}

		// Event not in list, fetch it directly
		if (!isLoading) {
			supabase
				.from('events')
				.select('*')
				.eq('id', eventIdFromUrl)
				.maybeSingle()
				.then(({ data, error }) => {
					if (data && !error) {
						// Transform the database event to EventObject format
						const eventObject: EventObject = {
							id: data.id,
							title: data.title,
							description: data.description,
							start: new Date(data.start_time),
							end: new Date(data.end_time),
							color: data.color,
							location: data.location,
							url: data.url,
							status: data.status,
							priority: data.priority,
							all_day: data.all_day,
							metadata: data.metadata
						};
						setSelectedEventFromUrl(eventObject);
						trackVisit('event', data.id, data.title);
					}
				});
		}
	}, [eventIdFromUrl, calendarEvents, isLoading, selectedEventFromUrl]);

	// Track event visit when CalendarKit opens an event
	useEffect(() => {
		if (!calendarKitOpenEventId) return;
		const event = calendarEvents.find(e => e.id === calendarKitOpenEventId);
		if (event)
			trackVisit('event', event.id, event.title);
	}, [calendarKitOpenEventId, calendarEvents, trackVisit]);

	// Handle closing event and updating URL
	const handleCloseEventFromUrl = () => {
		setSelectedEventFromUrl(null);
		// Remove event parameter from URL
		searchParams.delete('event');
		setSearchParams(searchParams, { replace: true });
	};

	// Handle opening event with URL parameter
	const handleOpenEvent = useCallback((eventId: string) => {
		setSearchParams(prev => {
			const currentParams = Object.fromEntries(prev.entries());
			return { ...currentParams, event: eventId };
		}, { replace: false });
	}, [setSearchParams]);

	// Get working hours from calendar settings (from SettingsProvider/Supabase)
	const workDayStart = calendarSettings?.workDayStart ?? 9;
	const workDayEnd = calendarSettings?.workDayEnd ?? 17;

	// Create locale with custom week start day
	const calendarLocale: Locale = {
		...enGB,
		options: {
			...enGB.options,
			weekStartsOn: (calendarSettings?.firstDayOfWeek ?? 1) as 0 | 1 | 2 | 3 | 4 | 5 | 6
		}
	};

	// Debug timezone
	console.log('[Calendar] Timezone from settings:', timezone);

	const {
		handleEventCreate,
		handleEventUpdate,
		handleEventDelete,
		isDeletingEvent
	} = useCalendarState();

	// Find the main scrollable container in CalendarKit
	const findScrollableContainer = useCallback((): HTMLElement | null => {
		if (!schedulerContainerRef.current) return null;

		// Strategy 1: Look for elements with overflow-y: auto or scroll
		const allElements = schedulerContainerRef.current.querySelectorAll('*');
		let bestMatch: HTMLElement | null = null;
		let bestScore = 0;

		allElements.forEach((el) => {
			const element = el as HTMLElement;
			const computedStyle = window.getComputedStyle(element);
			const overflowY = computedStyle.overflowY;

			// Check if element has scrollable overflow
			const isScrollable = overflowY === 'auto' || overflowY === 'scroll';
			const hasContent = element.scrollHeight > 100;
			const hasReasonableHeight = element.clientHeight > 100;

			if (isScrollable && hasContent && hasReasonableHeight) {
				// Prefer elements with more scroll content (taller scrollHeight)
				const score = element.scrollHeight;
				if (score > bestScore) {
					bestScore = score;
					bestMatch = element;
				}
			}
		});

		// Strategy 2: If no scrollable element found, look for the time grid area
		if (!bestMatch) {
			// Try common CalendarKit class patterns
			const candidates = schedulerContainerRef.current.querySelectorAll(
				'[class*="grid"], [class*="time"], [class*="body"], [class*="content"], [class*="scroll"]'
			);

			candidates.forEach((el) => {
				const element = el as HTMLElement;
				if (element.scrollHeight > bestScore && element.clientHeight > 100) {
					bestScore = element.scrollHeight;
					bestMatch = element;
				}
			});
		}

		console.log('findScrollableContainer found:', bestMatch, 'scrollHeight:', bestMatch?.scrollHeight, 'clientHeight:', bestMatch?.clientHeight);
		return bestMatch;
	}, []);

	// Save current scroll position
	const saveScrollPosition = useCallback(() => {
		const container = findScrollableContainer();
		if (container) {
			lastScrollPositionRef.current = container.scrollTop;
		}
	}, [findScrollableContainer]);

	// Restore scroll position (or scroll to working hours if no saved position)
	const restoreOrScrollToWorkingHours = useCallback((forceWorkingHours = false) => {
		const container = findScrollableContainer();
		if (!container) {
			console.log('restoreOrScrollToWorkingHours: No container found');
			return;
		}

		// If showing all hours and we have a saved position, restore it
		if (showAllHours && lastScrollPositionRef.current !== null && !forceWorkingHours) {
			console.log('Restoring saved scroll position:', lastScrollPositionRef.current);
			container.scrollTop = lastScrollPositionRef.current;
			return;
		}

		// If not showing all hours, scroll to working hours start
		if (!showAllHours || forceWorkingHours) {
			// Calculate scroll position
			// CalendarKit uses approximately 60px per hour
			// We need to scroll so the workDayStart hour is visible just below the header
			// The header is approximately 80px tall
			const pixelsPerHour = 60;
			const headerOffset = 80; // Account for the sticky day header
			const scrollTop = Math.max(0, (workDayStart * pixelsPerHour) - headerOffset);

			console.log('Scrolling to working hours:', {
				workDayStart,
				scrollTop,
				headerOffset,
				containerScrollHeight: container.scrollHeight,
				containerClientHeight: container.clientHeight,
				currentScrollTop: container.scrollTop
			});

			// Force the scroll
			container.scrollTop = scrollTop;

			// Double-check it was applied
			setTimeout(() => {
				console.log('After scroll, scrollTop is:', container.scrollTop);
			}, 50);

			lastScrollPositionRef.current = scrollTop;
		}
	}, [findScrollableContainer, showAllHours, workDayStart]);

	// Transform EventObject to CalendarEvent format
	const transformedEvents: CalendarEvent[] = useMemo(() => {
		return calendarEvents.map((event: EventObject) => ({
			id: event.id,
			title: event.title,
			start: event.start,
			end: event.end,
			description: event.description,
			color: event.color || "#9b87f5",
			allDay: event.all_day || false,
			// Store additional metadata in a way that can be preserved
			...(event.metadata && {
				// We can store custom fields in the event object
				location: event.location,
				url: event.url,
				status: event.status,
				priority: event.priority,
			}),
		}));
	}, [calendarEvents]);

	// Events to pass to Scheduler (exclude all-day events for week/day views)
	const schedulerEvents = useMemo(() => {
		if (view === "month") return transformedEvents;
		return transformedEvents.filter(e => !e.allDay);
	}, [transformedEvents, view]);

	// All-day events for custom banner rendering
	const allDayEvents = useMemo(() => {
		if (view === "month") return [] as CalendarEvent[];
		return transformedEvents.filter(e => e.allDay);
	}, [transformedEvents, view]);

	// Handler for event creation from CalendarKit
	const handleCalendarKitEventCreate = async (event: Partial<CalendarEvent>) => {
		setIsUpdating(true);
		try {
			// Find the original event to get metadata if editing
			const originalEvent = calendarEvents.find(e => e.id === event.id);
			const metadata = originalEvent?.metadata || {};
			
			await handleEventCreate({
				title: event.title || "",
				start: event.start || new Date(),
				end: event.end || new Date(),
				description: event.description,
				color: event.color,
				location: (event as any).location,
				url: (event as any).url,
				status: (event as any).status || 'confirmed',
				priority: (event as any).priority || 'medium',
				all_day: event.allDay || false,
				relations: {
					candidates: Array.isArray(metadata.candidates) ? metadata.candidates : [],
					offers: Array.isArray(metadata.offers) ? metadata.offers : [],
					companies: Array.isArray(metadata.companies) ? metadata.companies : [],
					matches: Array.isArray(metadata.matches) ? metadata.matches : [],
					contacts: Array.isArray(metadata.contacts) ? metadata.contacts : []
				},
				linkedUsers: Array.isArray(metadata.linkedUsers) ? metadata.linkedUsers : []
			});
			await refetch();
		} catch (error) {
			console.error('Error creating event:', error);
		} finally {
			setIsUpdating(false);
		}
	};

	// Track the last operation to prevent duplicate updates
	// The library calls BOTH onEventResize AND onEventUpdate when resizing
	const lastOperationRef = useRef<{ type: string; timestamp: number; eventId: string } | null>(null);

	// Handler for event resize (extend/shrink duration)
	const handleCalendarKitEventResize = async (event: CalendarEvent, start: Date, end: Date) => {
		console.log('EVENT RESIZE triggered:', { event: event.title, start, end });

		const originalEvent = calendarEvents.find(e => e.id === event.id);
		if (!originalEvent) {
			console.warn('Original event not found for resize');
			return;
		}

		// Mark that we're handling a resize
		lastOperationRef.current = {
			type: 'resize',
			timestamp: Date.now(),
			eventId: event.id
		};

		// Save scroll position before update
		saveScrollPosition();
		setIsUpdating(true);
		try {
			const metadata = originalEvent?.metadata || {};

			await handleEventUpdate(event.id, {
				title: event.title,
				start: start,
				end: end,
				description: event.description,
				color: event.color,
				location: (event as any).location,
				url: (event as any).url,
				status: (event as any).status || 'confirmed',
				priority: (event as any).priority || 'medium',
				all_day: event.allDay || false,
				relations: {
					candidates: Array.isArray(metadata.candidates) ? metadata.candidates : [],
					offers: Array.isArray(metadata.offers) ? metadata.offers : [],
					companies: Array.isArray(metadata.companies) ? metadata.companies : [],
					matches: Array.isArray(metadata.matches) ? metadata.matches : [],
					contacts: Array.isArray(metadata.contacts) ? metadata.contacts : []
				}
			});
			await refetch();
		} catch (error) {
			console.error('Error resizing event:', error);
		} finally {
			setIsUpdating(false);
		}
	};

	// Handler for general event update (handles drag/move and form edits)
	const handleCalendarKitEventUpdate = async (event: CalendarEvent) => {
		console.log('General EVENT UPDATE triggered:', { event: event.title, start: event.start, end: event.end });

		// Check if this is a duplicate call from resize operation
		const lastOp = lastOperationRef.current;
		if (lastOp &&
			lastOp.type === 'resize' &&
			lastOp.eventId === event.id &&
			Date.now() - lastOp.timestamp < 100) {
			console.log('Skipping duplicate update (already handled by resize)');
			return;
		}

		const originalEvent = calendarEvents.find(e => e.id === event.id);
		if (!originalEvent) {
			console.warn('Original event not found for update');
			return;
		}

		// Determine if this is a drag operation (move) or something else
		const startChanged = originalEvent.start.getTime() !== event.start.getTime();
		const endChanged = originalEvent.end.getTime() !== event.end.getTime();
		const durationChanged = (event.end.getTime() - event.start.getTime()) !==
								(originalEvent.end.getTime() - originalEvent.start.getTime());

		if (startChanged && !durationChanged) {
			console.log('Detected MOVE operation');
		} else if (endChanged && startChanged) {
			console.log('Detected potential MOVE with duration change');
		} else {
			console.log('Detected other update');
		}

		// Save scroll position before update
		saveScrollPosition();
		setIsUpdating(true);
		try {
			const metadata = originalEvent?.metadata || {};

			await handleEventUpdate(event.id, {
				title: event.title,
				start: event.start,
				end: event.end,
				description: event.description,
				color: event.color,
				location: (event as any).location,
				url: (event as any).url,
				status: (event as any).status || 'confirmed',
				priority: (event as any).priority || 'medium',
				all_day: event.allDay || false,
				relations: {
					candidates: Array.isArray(metadata.candidates) ? metadata.candidates : [],
					offers: Array.isArray(metadata.offers) ? metadata.offers : [],
					companies: Array.isArray(metadata.companies) ? metadata.companies : [],
					matches: Array.isArray(metadata.matches) ? metadata.matches : [],
					contacts: Array.isArray(metadata.contacts) ? metadata.contacts : []
				}
			});
			await refetch();
		} catch (error) {
			console.error('Error updating event:', error);
		} finally {
			setIsUpdating(false);
		}
	};

	// Handler for event deletion from CalendarKit
	const handleCalendarKitEventDelete = async (eventId: string) => {
		// Save scroll position before update
		saveScrollPosition();
		setIsUpdating(true);
		try {
			await handleEventDelete(eventId);
			await refetch();
		} catch (error) {
			console.error('Error deleting event:', error);
		} finally {
			setIsUpdating(false);
		}
	};


	const handleEventCreateWithRefetch = async (eventData: any) => {
		setIsUpdating(true);
		try {
			await handleEventCreate(eventData);
			await refetch();
			setShowCreateModal(false);
		} catch (error) {
			console.error('Error creating event:', error);
		} finally {
			setIsUpdating(false);
		}
	};

	// Map view mode from CalendarKit to our internal format
	const mapViewMode = (view: ViewType): "day" | "week" | "month" => {
		if (view === "day") return "day";
		if (view === "week") return "week";
		if (view === "month") return "month";
		return "week";
	};

	// Inject Settings button into CalendarKit's sidebar
	useEffect(() => {
		let injecting = false;

		const renderButtons = () => {
			if (!settingsButtonRootRef.current) return;
			settingsButtonRootRef.current.render(
				<div className="space-y-2">
					<Button
						variant="outline"
						size="sm"
						onClick={() => setShowAllHours(!showAllHours)}
						className="w-full"
						title={showAllHours
							? `Show working hours (${workDayStart}:00 - ${workDayEnd}:00)`
							: 'Show all 24 hours'}
					>
						{showAllHours ? (
							<>
								<Shrink className="h-4 w-4 mr-2" />
								Working Hours
							</>
						) : (
							<>
								<Expand className="h-4 w-4 mr-2" />
								All Hours
							</>
						)}
					</Button>
					<Button
						variant="outline"
						size="sm"
						onClick={() => setShowSettings(true)}
						className="w-full"
					>
						<Settings className="h-4 w-4 mr-2" />
						Settings
					</Button>
				</div>
			);
		};

		const injectSettingsButton = () => {
			if (!schedulerContainerRef.current || injecting) return;

			// If container already exists and is still in the DOM, just re-render
			const existing = schedulerContainerRef.current.querySelector('.calendar-settings-btn-injected');
			if (existing && settingsButtonRootRef.current) {
				renderButtons();
				return;
			}

			injecting = true;

			// Clean up orphaned state (CalendarKit may have re-rendered its sidebar)
			if (existing) existing.remove();
			if (settingsButtonRootRef.current) {
				const oldRoot = settingsButtonRootRef.current;
				settingsButtonRootRef.current = null;
				setTimeout(() => oldRoot.unmount(), 0);
			}

			// Find the sidebar container (has scrollbar-hide class)
			const sidebar = schedulerContainerRef.current.querySelector('[class*="scrollbar-hide"]');
			if (!sidebar) {
				injecting = false;
				return;
			}

			// Create container and root
			const buttonContainer = document.createElement('div');
			buttonContainer.className = 'calendar-settings-btn-injected';
			buttonContainer.style.cssText = 'width: 100%; padding: 0 1rem; margin-top: 1.25rem;';
			sidebar.appendChild(buttonContainer);
			settingsButtonRootRef.current = createRoot(buttonContainer);
			renderButtons();

			setTimeout(() => { injecting = false; }, 50);
		};

		// Try multiple times with increasing delays to catch CalendarKit rendering
		const timers: NodeJS.Timeout[] = [];
		[200, 500, 1000, 2000].forEach((delay) => {
			const timer = setTimeout(injectSettingsButton, delay);
			timers.push(timer);
		});

		// Observe DOM changes with debounce
		let debounceTimer: NodeJS.Timeout | null = null;
		const observer = new MutationObserver(() => {
			if (injecting) return;
			if (debounceTimer) clearTimeout(debounceTimer);
			debounceTimer = setTimeout(injectSettingsButton, 300);
		});

		if (schedulerContainerRef.current) {
			observer.observe(schedulerContainerRef.current, {
				childList: true,
				subtree: true,
				attributes: false,
				characterData: false
			});
		}

		return () => {
			timers.forEach(timer => clearTimeout(timer));
			if (debounceTimer) clearTimeout(debounceTimer);
			observer.disconnect();
			// Defer unmount to avoid "synchronously unmount during render" warning
			if (settingsButtonRootRef.current) {
				const root = settingsButtonRootRef.current;
				settingsButtonRootRef.current = null;
				setTimeout(() => root.unmount(), 0);
			}
		};
	}, [view, date, setShowSettings, showAllHours, workDayStart, workDayEnd]);

	// Initial scroll to working hours when calendar first loads
	useEffect(() => {
		if (isLoading || hasInitialScrolled.current) return;

		// Wait for CalendarKit to fully render, then scroll
		const timers = [100, 300, 500, 800].map(delay =>
			setTimeout(() => {
				const container = findScrollableContainer();
				if (container && !hasInitialScrolled.current) {
					restoreOrScrollToWorkingHours(true);
					hasInitialScrolled.current = true;
				}
			}, delay)
		);

		return () => {
			timers.forEach(clearTimeout);
		};
	}, [isLoading, findScrollableContainer, restoreOrScrollToWorkingHours]);

	// When view or date changes, reset and scroll to working hours
	useEffect(() => {
		// Reset initial scroll flag when view or date changes
		hasInitialScrolled.current = false;
		lastScrollPositionRef.current = null;

		// Scroll to working hours after a short delay
		const timer = setTimeout(() => {
			restoreOrScrollToWorkingHours(true);
			hasInitialScrolled.current = true;
		}, 300);

		return () => clearTimeout(timer);
	}, [view, date, restoreOrScrollToWorkingHours]);

	// When showAllHours changes, adjust scroll accordingly
	useEffect(() => {
		// If switching to working hours mode, scroll to working hours
		if (!showAllHours) {
			setTimeout(() => {
				restoreOrScrollToWorkingHours(true);
			}, 100);
		}
	}, [showAllHours, restoreOrScrollToWorkingHours]);

	// Restore scroll position after updates complete
	useEffect(() => {
		if (!isUpdating && lastScrollPositionRef.current !== null) {
			// After update completes, restore scroll position
			const timer = setTimeout(() => {
				restoreOrScrollToWorkingHours();
			}, 100);
			return () => clearTimeout(timer);
		}
	}, [isUpdating, restoreOrScrollToWorkingHours]);

	// Apply working hours view constraints to the scrollable container
	useEffect(() => {
		if (!schedulerContainerRef.current) return;

		const applyWorkingHoursConstraints = () => {
			const container = findScrollableContainer();
			if (!container) return;

			if (!showAllHours) {
				// Calculate the height to show only working hours
				// Each hour is approximately 60px
				const workingHoursCount = workDayEnd - workDayStart;
				const targetHeight = (workingHoursCount * 60) + 20; // +20 for some padding

				// Apply max-height to constrain visible area
				container.style.maxHeight = `${targetHeight}px`;
				container.style.overflowY = 'auto';
			} else {
				// Remove constraints when showing all hours
				container.style.maxHeight = '';
				container.style.overflowY = '';
			}
		};

		// Apply after a short delay to ensure CalendarKit has rendered
		const timers = [100, 300, 500].map(delay =>
			setTimeout(applyWorkingHoursConstraints, delay)
		);

		return () => {
			timers.forEach(clearTimeout);
			// Clean up styles when unmounting or changing mode
			const container = findScrollableContainer();
			if (container) {
				container.style.maxHeight = '';
				container.style.overflowY = '';
			}
		};
	}, [showAllHours, workDayStart, workDayEnd, findScrollableContainer, view, date]);

	// Inject all-day events row into CalendarKit's DOM
	useEffect(() => {
		if (!schedulerContainerRef.current) return;
		let injecting = false;

		const renderAllDayContent = (weekView: boolean) => {
			if (!allDayRowRootRef.current) return;

			if (weekView) {
				// Use firstDayOfWeek from settings (1 = Monday for Europe, 0 = Sunday for US)
				const firstDayOfWeek = calendarSettings?.firstDayOfWeek ?? 1;
				const weekStart = startOfWeek(date, { weekStartsOn: firstDayOfWeek as 0 | 1 | 2 | 3 | 4 | 5 | 6 });
				const weekDays = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

				allDayRowRootRef.current.render(
					<div className="flex border-b border-border/30 bg-background/95 backdrop-blur-sm">
						<div className="flex-none w-16 text-center text-[10px] font-medium text-muted-foreground/70 flex items-center justify-center border-r border-border/30 py-1">
							All day
						</div>
						<div className="flex-1 grid grid-cols-7 max-h-[80px] overflow-y-auto">
							{weekDays.map((day, index) => {
								const dayAllDayEvents = allDayEvents.filter(event => {
									const eventStart = startOfDay(event.start);
									const eventEnd = event.end ? startOfDay(event.end) : eventStart;
									const dayDate = startOfDay(day);
									// For all-day events, end time is exclusive (midnight of next day)
									return dayDate >= eventStart && dayDate < eventEnd;
								});
								return (
									<div
										key={day.toISOString()}
										className={`py-1 px-0.5 ${index > 0 ? 'border-l border-border/30' : ''} cursor-pointer hover:bg-muted/30 transition-colors min-h-[40px]`}
										onClick={(e) => {
											// Only trigger create if clicking on empty space (not on an event)
											if ((e.target as HTMLElement).classList.contains('py-1')) {
												setCreateAllDayDate(startOfDay(day));
											}
										}}
										title="Click to create all-day event"
									>
										{dayAllDayEvents.map(event => (
											<div
												key={event.id}
												className="text-[11px] px-1.5 py-0.5 rounded truncate mb-0.5 cursor-pointer hover:opacity-80 transition-opacity"
												style={{
													backgroundColor: event.color ? `${event.color}20` : undefined,
													borderLeft: `3px solid ${event.color || 'hsl(var(--primary))'}`,
												}}
												onClick={(e) => {
													e.stopPropagation();
													document.dispatchEvent(new CustomEvent('allday-event-click', { detail: event.id }));
												}}
												title={event.title}
											>
												{event.title}
											</div>
										))}
									</div>
								);
							})}
						</div>
					</div>
				);
			} else {
				// For day view, show events that start on or span across this day
				const dayAllDayEvents = allDayEvents.filter(event => {
					const eventStart = startOfDay(event.start);
					const eventEnd = event.end ? startOfDay(event.end) : eventStart;
					const dayDate = startOfDay(date);
					// For all-day events, end time is exclusive (midnight of next day)
					return dayDate >= eventStart && dayDate < eventEnd;
				});

				allDayRowRootRef.current.render(
					<div
						className="flex items-center gap-2 border-b border-border/30 bg-background/95 px-6 py-2 cursor-pointer hover:bg-muted/30 transition-colors min-h-[40px]"
						onClick={(e) => {
							// Only trigger create if clicking on empty space (not on an event)
							if ((e.target as HTMLElement).classList.contains('flex')) {
								setCreateAllDayDate(startOfDay(date));
							}
						}}
						title="Click to create all-day event"
					>
						<span className="text-[10px] font-medium text-muted-foreground/70 whitespace-nowrap">All day</span>
						<div className="flex flex-wrap gap-1">
							{dayAllDayEvents.length > 0 ? (
								dayAllDayEvents.map(event => (
									<div
										key={event.id}
										className="text-[11px] px-2 py-0.5 rounded cursor-pointer hover:opacity-80 transition-opacity"
										style={{
											backgroundColor: event.color ? `${event.color}20` : undefined,
											borderLeft: `3px solid ${event.color || 'hsl(var(--primary))'}`,
										}}
										onClick={(e) => {
											e.stopPropagation();
											document.dispatchEvent(new CustomEvent('allday-event-click', { detail: event.id }));
										}}
										title={event.title}
									>
										{event.title}
									</div>
								))
							) : (
								<span className="text-[10px] text-muted-foreground/50 italic">Click to add all-day event</span>
							)}
						</div>
					</div>
				);
			}
		};

		const injectAllDayRow = () => {
			if (!schedulerContainerRef.current || injecting) return;
			injecting = true;

			const existing = schedulerContainerRef.current.querySelector('.all-day-events-injected');

			// Clean up stale root if container was removed by CalendarKit re-render
			if (!existing && allDayRowRootRef.current) {
				const oldRoot = allDayRowRootRef.current;
				allDayRowRootRef.current = null;
				setTimeout(() => oldRoot.unmount(), 0);
			}

			// If no all-day events, clean up and return
			if (allDayEvents.length === 0) {
				if (existing) {
					if (allDayRowRootRef.current) {
						const root = allDayRowRootRef.current;
						allDayRowRootRef.current = null;
						setTimeout(() => root.unmount(), 0);
					}
					existing.remove();
				}
				setTimeout(() => { injecting = false; }, 50);
				return;
			}

			if (view === "week") {
				// Find the WeekView scroll container (has scroll-smooth class)
				const scrollContainer = schedulerContainerRef.current.querySelector('[class*="scroll-smooth"]');
				if (!scrollContainer) {
					setTimeout(() => { injecting = false; }, 50);
					return;
				}

				const stickyHeader = scrollContainer.querySelector('[class*="sticky"]');
				if (!stickyHeader) {
					setTimeout(() => { injecting = false; }, 50);
					return;
				}

				if (existing && allDayRowRootRef.current) {
					renderAllDayContent(true);
				} else {
					if (existing) existing.remove();
					const container = document.createElement('div');
					container.className = 'all-day-events-injected';
					const headerHeight = (stickyHeader as HTMLElement).getBoundingClientRect().height;
					container.style.cssText = `position: sticky; top: ${headerHeight}px; z-index: 15;`;

					if (stickyHeader.nextSibling)
						scrollContainer.insertBefore(container, stickyHeader.nextSibling);
					else
						scrollContainer.appendChild(container);

					allDayRowRootRef.current = createRoot(container);
					renderAllDayContent(true);
				}
			} else if (view === "day") {
				// Find the DayView header (unique: has shrink-0 and text-center)
				const dayHeader = schedulerContainerRef.current.querySelector('[class*="shrink-0"][class*="text-center"]');
				if (!dayHeader || !dayHeader.parentElement) {
					setTimeout(() => { injecting = false; }, 50);
					return;
				}

				if (existing && allDayRowRootRef.current) {
					renderAllDayContent(false);
				} else {
					if (existing) existing.remove();
					const container = document.createElement('div');
					container.className = 'all-day-events-injected';

					if (dayHeader.nextSibling)
						dayHeader.parentElement.insertBefore(container, dayHeader.nextSibling);
					else
						dayHeader.parentElement.appendChild(container);

					allDayRowRootRef.current = createRoot(container);
					renderAllDayContent(false);
				}
			}

			setTimeout(() => { injecting = false; }, 50);
		};

		const timers: NodeJS.Timeout[] = [];
		// More aggressive retry schedule for initial mount
		[100, 200, 400, 600, 1000, 1500, 2000].forEach(delay => {
			timers.push(setTimeout(injectAllDayRow, delay));
		});

		let debounceTimer: NodeJS.Timeout | null = null;
		const observer = new MutationObserver(() => {
			if (injecting) return;
			if (debounceTimer) clearTimeout(debounceTimer);
			debounceTimer = setTimeout(injectAllDayRow, 300);
		});

		if (schedulerContainerRef.current) {
			observer.observe(schedulerContainerRef.current, {
				childList: true,
				subtree: true,
				attributes: false,
				characterData: false,
			});
		}

		return () => {
			timers.forEach(clearTimeout);
			if (debounceTimer) clearTimeout(debounceTimer);
			observer.disconnect();
			if (allDayRowRootRef.current) {
				const root = allDayRowRootRef.current;
				allDayRowRootRef.current = null;
				setTimeout(() => root.unmount(), 0);
			}
		};
	}, [allDayEvents, view, date, calendarSettings]);

	// Listen for clicks on all-day event chips
	useEffect(() => {
		const handler = (e: Event) => {
			const eventId = (e as CustomEvent).detail;
			if (eventId)
				handleOpenEvent(eventId);
		};
		document.addEventListener('allday-event-click', handler);
		return () => document.removeEventListener('allday-event-click', handler);
	}, [handleOpenEvent]);

	// Only show full loading state for initial load, not for updates
	if (isLoading) {
		return <div className="h-[calc(100vh-4rem)] flex flex-col">
			<div className="flex-1 flex items-center justify-center">
				<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
			</div>
		</div>;
	}

	return (
		<div className="h-[calc(100vh-4rem)] flex flex-col relative">
			<style>{`
				/* Hide CalendarKit's Calendars section in sidebar */
				[class*="scrollbar-hide"] > [class*="flex-1"][class*="space-y-5"] {
					display: none !important;
				}
				/* Hide CalendarKit's Timezone section in sidebar */
				[class*="scrollbar-hide"] > [class*="mt-auto"][class*="pt-5"] {
					display: none !important;
				}
				/* Hide CalendarKit's current time indicator (wrong timezone positioning) */
				span.text-\\[10px\\].font-bold.text-primary-foreground {
					display: none !important;
				}
			`}</style>
			{/* Overlay loading indicator for updates - doesn't unmount the Scheduler */}
			{isUpdating && (
				<div className="absolute inset-0 bg-white/50 dark:bg-gray-900/50 z-50 flex items-center justify-center pointer-events-none">
					<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
				</div>
			)}
			<div
				className="flex-1 overflow-hidden"
				ref={schedulerContainerRef}
			>
				<Scheduler
					events={schedulerEvents}
					view={view}
					onViewChange={setView}
					date={date}
					onDateChange={setDate}
					timezone={timezone || "UTC"}
					locale={calendarLocale}
					calendars={[]}
					onEventCreate={(event) => {
						// When clicking on a time slot or using CalendarKit's create button, open our custom modal
						setSelectedSlotDate(event.start);
						setShowCreateModal(true);
					}}
					onEventResize={handleCalendarKitEventResize}
					onEventUpdate={handleCalendarKitEventUpdate}
					onEventDelete={handleCalendarKitEventDelete}
					isLoading={isLoading || isUpdating}
					renderEventForm={(props) => (
						<EventFormRenderer
							{...props}
							calendarEvents={calendarEvents}
							refetch={refetch}
							isDeletingEvent={isDeletingEvent}
							handleCalendarKitEventDelete={handleCalendarKitEventDelete}
							onOpenEvent={handleOpenEvent}
							onCloseEvent={handleCloseEventFromUrl}
							onTrackCalendarKitEvent={setCalendarKitOpenEventId}
						/>
					)}
				/>
			</div>

			<CalendarSettings isOpen={showSettings} onClose={() => setShowSettings(false)} />

			<CreateEventModal 
				isOpen={showCreateModal} 
				onClose={() => {
					setShowCreateModal(false);
					setSelectedSlotDate(undefined);
				}} 
				onSave={async () => {
					await refetch();
					setShowCreateModal(false);
					setSelectedSlotDate(undefined);
				}} 
				initialDate={selectedSlotDate}
				initialData={selectedSlotDate ? {
					id: '',
					title: '',
					description: '',
					start: selectedSlotDate,
					end: new Date(selectedSlotDate.getTime() + 60 * 60 * 1000), // 1 hour later
					color: '#9b87f5',
					metadata: {
						candidates: [],
						offers: [],
						companies: [],
						matches: [],
						contacts: []
					}
				} : undefined}
			/>

			{selectedEventFromUrl && selectedEventFromUrl.id !== calendarKitOpenEventId && (
				<CreateEventModal
					initialData={{
						id: selectedEventFromUrl.id,
						title: selectedEventFromUrl.title,
						description: selectedEventFromUrl.description,
						start: selectedEventFromUrl.start,
						end: selectedEventFromUrl.end,
						color: selectedEventFromUrl.color,
						location: selectedEventFromUrl.location,
						url: selectedEventFromUrl.url,
						status: selectedEventFromUrl.status,
						priority: selectedEventFromUrl.priority,
						all_day: selectedEventFromUrl.all_day,
						metadata: selectedEventFromUrl.metadata || {
							candidates: [],
							offers: [],
							companies: [],
							matches: [],
							contacts: []
						}
					}}
					isOpen={true}
					onClose={handleCloseEventFromUrl}
					onSave={async () => {
						await refetch();
						handleCloseEventFromUrl();
					}}
					onDelete={async () => {
						await handleCalendarKitEventDelete(selectedEventFromUrl.id);
						handleCloseEventFromUrl();
					}}
					isEditing={true}
					isDeleting={isDeletingEvent}
				/>
			)}

			{createAllDayDate && (
				<CreateEventModal
					isOpen={true}
					onClose={() => setCreateAllDayDate(null)}
					onSave={async () => {
						await refetch();
						setCreateAllDayDate(null);
					}}
					initialDate={createAllDayDate}
					initialData={{
						id: '',
						title: '',
						description: '',
						start: createAllDayDate,
						end: createAllDayDate,
						color: '#9b87f5',
						metadata: {
							all_day: true,
							candidates: [],
							offers: [],
							companies: [],
							matches: [],
							contacts: []
						}
					}}
				/>
			)}
		</div>
	);
};

export default Calendar;
